import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, KeyRound, LogOut, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

function Card({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] dark:border-white/10 dark:bg-white/[0.03]" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <Icon size={16} className="text-slate-400" /> {title}
      </h2>
      {children}
    </section>
  );
}

export default function ParentSettings() {
  const navigate = useNavigate();
  const { user, logout, refresh } = useAuth();

  const [email, setEmail] = useState(user?.email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.patch("/auth/email", { email });
      await refresh();
      toast.success("Adresse e-mail mise à jour.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const savePassword = async () => {
    if (!oldPw || !newPw || !confirmPw) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw, new_password: newPw, confirm_password: confirmPw,
      });
      setOldPw(""); setNewPw(""); setConfirmPw("");
      await refresh();
      toast.success("Mot de passe modifié avec succès.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] dark:bg-[#0A0A0A]">
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white/85 backdrop-blur-xl dark:border-white/10 dark:bg-[#0A0A0A]/85">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/parent")}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-transform hover:scale-95 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            data-testid="settings-back" aria-label="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">Paramètres</h1>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex flex-col gap-5"
        >
          <Card icon={Mail} title="Adresse e-mail" testid="email-card">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">Votre adresse e-mail</Label>
                <Input
                  id="email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre.email@exemple.fr"
                  data-testid="settings-email-input"
                />
                <p className="text-xs text-slate-400">Utilisée pour recevoir les notifications de l'enseignant.</p>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveEmail} disabled={savingEmail} className="rounded-xl" data-testid="save-email-button">
                  {savingEmail ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </div>
          </Card>

          <Card icon={KeyRound} title="Mot de passe" testid="password-card">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="old_pw">Ancien mot de passe</Label>
                <Input id="old_pw" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="old-password-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="new_pw">Nouveau mot de passe</Label>
                <Input id="new_pw" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="confirm_pw">Confirmer le nouveau mot de passe</Label>
                <Input id="confirm_pw" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
              </div>
              <div className="flex justify-end">
                <Button onClick={savePassword} disabled={savingPw} className="rounded-xl" data-testid="save-password-button">
                  {savingPw ? <Loader2 size={16} className="mr-2 animate-spin" /> : <KeyRound size={16} className="mr-2" />}
                  Modifier le mot de passe
                </Button>
              </div>
            </div>
          </Card>

          <button
            onClick={logout}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-sm font-medium text-slate-700 transition-transform hover:scale-[0.99] dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            data-testid="settings-logout-button"
          >
            <LogOut size={16} /> Se déconnecter
          </button>
        </motion.div>
      </main>
    </div>
  );
}
